import axios, { type AxiosRequestConfig, type Method } from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';
import Cookies from 'js-cookie';
import NProgress from 'nprogress';
import qs from 'qs';

import { filterNull, isBot } from '@/assets/js/common';
import { BLOCKED_API_PATTERNS } from '@/assets/js/constants';
import { recordSsrBackendRequest } from '@/server/utils/ssr-request-logger/context';

import { encryptoData } from './encrypt';
import { toErrorString } from '../server/utils/error-utils';

// 导入自动生成的接口类型文件
import type { ApiParamsDefinition, ApiResponseDefinition } from './types/api-definition';

// 定义请求配置类型
interface RequestConfig {
  errorMsg?: boolean;
  successMsg?: boolean;
  successWithoutCode?: boolean;
  msgType?: 'default' | 'box';
  duration?: number;
  isEncrypt?: boolean;
  canRepeated?: boolean;
  storeId?: string | number;
}

// 创建axios实例
export const http = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
});

// 请求之前
http.interceptors.request.use(
  (config) => {
    if (process.client) {
      NProgress.start();
      if (window.__isLcpApiLogEnabled__ && window.__lcpApiLogs__) {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const logUrl =
          (config as any)._lcpLogUrl ||
          `${config.method?.toUpperCase() || 'GET'}:${config.url || ''}`;
        (config as any)._lcpLogIndex = window.__lcpApiLogs__.length;
        /* eslint-enable @typescript-eslint/no-explicit-any */
        window.__lcpApiLogs__.push({
          url: logUrl,
          startTime: Math.round(performance.now()),
        });
      }
    }
    let token = '';
    // 从客户端持久化中读取鉴权信息，避免引入 store 造成循环依赖. cookie 和 localStorage 应该已经包含了 token
    if (process.client) {
      token = Cookies.get('voo-token') || localStorage.getItem('voo-token') || '';
    }
    let visitorId = '';
    if (process.client) {
      visitorId = localStorage.getItem('voo-visitorId') || '';
    }
    if (token) {
      config.headers.Authorization = `${token} `;
    }
    if (visitorId) {
      config.headers['visitor-id'] = visitorId;
    }
    /* store-id 设备端标识：1pc 4wap 5iOS 6Android */
    config.headers['store-id'] = 1;
    if (process.server) {
      config.headers['X-Client-Type'] = 'ssr';
    }
    try {
      if (process.client) {
        const distinctId = window.sensors?.store?.getDistinctId(); // 获取神策的登录 ID
        const anonymousId = window.sensors?.quick('getAnonymousID'); // 获取神策的匿名 ID
        if (distinctId) {
          Cookies.set('voo-distinctId', distinctId, { expires: 365 });
          config.headers['distinct-id'] = distinctId;
        }
        if (anonymousId) {
          Cookies.set('voo-anonymousId', anonymousId, { expires: 365 });
          config.headers['anonymous-id'] = anonymousId;
        }
        // 针对需要阻止爬虫的接口，则阻止请求（仅在客户端执行）
        const isBlocked =
          isBot() &&
          config.url &&
          BLOCKED_API_PATTERNS.some((pattern) => config.url?.includes(pattern));
        if (isBlocked) {
          // 取消请求，避免爬虫访问该接口
          const cancelToken = axios.CancelToken.source();
          cancelToken.cancel('Request blocked: crawler detected');
          config.cancelToken = cancelToken.token;
          return config;
        }
      }
    } catch (e) {
      console.warn(`[request中间件错误] 获取神策ID失败 detail=${toErrorString(e)}`);
    }
    /* 请求头添加version字段，便于统计当前客户端版本 */
    if (process.env.NODE_ENV !== 'local') {
      // 开发环境不给请求头添加version字段
      try {
        const currVersion = import.meta.env.VITE_VERSION || '';
        if (!config.headers.version && currVersion) config.headers.version = currVersion;
      } catch (error) {
        console.warn(`[request中间件错误] 获取版本号失败 detail=${toErrorString(error)}`);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
// 添加响应拦截器
http.interceptors.response.use(
  (response) => {
    if (process.client) {
      NProgress.done();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const index = (response.config as any)?._lcpLogIndex;
      if (typeof index === 'number' && window.__lcpApiLogs__ && window.__lcpApiLogs__[index]) {
        const endTime = Math.round(performance.now());
        window.__lcpApiLogs__[index].endTime = endTime;
        window.__lcpApiLogs__[index].duration = Math.round(
          endTime - window.__lcpApiLogs__[index].startTime,
        );
      }
    }
    // 返回想要的数据
    // 个别旧接口以 HTTP 200 + message 表示成功，不返回统一的 code 字段。
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const successWithoutCode = (response.config as any)?._businessConfig?.successWithoutCode;
    if (
      response.status === 200 &&
      (response.data?.code === 200 ||
        (successWithoutCode && response.data?.code === undefined && response.data?.message))
    ) {
      // 将接口返回的正确的数据抛出去
      return Promise.resolve(response.data);
    } else {
      // 接口请求成功了，但是有错误的信息提示
      // 报错时，把业务配置挂在数据上，方便插件读取
      // 注意：不能挂载完整的 response.config，因为它包含 transformRequest 等函数，
      // 在 SSR 中会导致 DevalueError: Cannot stringify a function
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errData = (response.data || {}) as any;
      errData.config = {
        url: response.config?.url || '',
        method: response.config?.method || '',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        _businessConfig: (response.config as any)?._businessConfig || {},
      };
      return Promise.reject(errData);
    }
  },
  (error) => {
    if (process.client) {
      NProgress.done();
      const config = error.config;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const index = (config as any)?._lcpLogIndex;
      if (typeof index === 'number' && window.__lcpApiLogs__ && window.__lcpApiLogs__[index]) {
        const endTime = Math.round(performance.now());
        window.__lcpApiLogs__[index].endTime = endTime;
        window.__lcpApiLogs__[index].duration = Math.round(
          endTime - window.__lcpApiLogs__[index].startTime,
        );
      }
    }
    return Promise.reject(error);
  },
);

/**
 * HTTP 请求方法（新的类型化调用方式）
 * @param url - 完整的请求URL，格式为 "METHOD:/path"，例如 "GET:/api/user" 或 "POST:/api/login"
 * @param params - 请求参数，根据 url 自动推断类型
 * @param config - 配置选项
 * @returns 返回 Promise，自动推断返回类型
 */
// eslint-disable-next-line vooglam-local/max-optional-params
export function httpRequest<URL extends keyof ApiParamsDefinition>(
  url: URL,
  params?: ApiParamsDefinition[URL],
  config?: {
    errorMsg?: boolean;
    successMsg?: boolean;
    msgType?: 'default' | 'box';
    duration?: number;
    isEncrypt?: boolean;
    canRepeated?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headers?: any;
    timeout?: number;
  },
): Promise<ApiResponseDefinition[URL]> {
  return new Promise((resolve, reject) => {
    try {
      // 解析 URL 获取请求方法和路径
      const [method, path] = (url as string).split(':') as [string, string];
      const requestConfig = config || {};

      // 处理请求参数（使用类型断言处理联合类型）
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const paramsAny = params as any;
      const requestData = paramsAny?.data;
      const requestParams = paramsAny?.params;

      // 处理路径参数（动态路由参数）
      let finalPath = path;
      if (finalPath?.match(/\{(.+?)\}/g)?.length) {
        finalPath.match(/\{(.+?)\}/g)?.forEach((item) => {
          if (item) {
            const key = item.substring(1, item?.length - 1);
            finalPath = finalPath?.replace(
              `/${item}`,
              paramsAny?.[key] !== null && paramsAny?.[key] !== undefined
                ? `/${paramsAny[key]}`
                : '',
            );
          }
        });
      }

      // 根据请求方法决定使用 data 还是 params
      const finalData = method.toUpperCase() === 'GET' ? requestParams || {} : requestData || {};

      // 调用原有的 axiosRequest 方法
      axiosRequest(
        finalPath,
        method.toLowerCase(),
        finalData,
        {
          errorMsg: requestConfig.errorMsg !== false,
          successMsg: requestConfig.successMsg === true,
          msgType: requestConfig.msgType || 'default',
          duration: requestConfig.duration,
          isEncrypt: requestConfig.isEncrypt,
          canRepeated: requestConfig.canRepeated,
        },
        requestConfig.headers || {},
      )
        .then((res) => {
          resolve(res as ApiResponseDefinition[URL]);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * HTTP 请求方法（原有签名，向后兼容）
 * @param url - 请求路径
 * @param method - HTTP 方法
 * @param data - 请求数据
 * @param config - 配置选项
 * @param headerConfig - 请求头配置
 * @returns 返回 Promise
 */
// eslint-disable-next-line vooglam-local/max-optional-params
export function request(
  url: string,
  method?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  config?: RequestConfig,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headerConfig?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  const methodToUse = method || 'get';
  const dataToUse = data || {};
  const configToUse = config || {};
  const headersToUse = headerConfig || {};
  return axiosRequest(url, methodToUse, dataToUse, configToUse, headersToUse);
}

// 封装axios的函数
const axiosRequest = <URL extends keyof ApiParamsDefinition>(
  url: string,
  method: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any,
  config: RequestConfig,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headerConfig: any,
): Promise<ApiResponseDefinition[URL]> => {
  const originalUrl = url;
  const runtimeConfig = useRuntimeConfig();
  const methodL = method.toLowerCase();
  if (methodL === 'del') {
    method = 'delete';
  }
  if (process.server) {
    if (process.env.APP_ENV === 'local') {
      url = process.env.VITE_LOCAL_TEST_API + url;
    } else {
      const serverBase = runtimeConfig.server.VUE_APP_SERVER_API;
      url = (serverBase || runtimeConfig.public.VUE_APP_API) + url;
    }
  } else {
    url = runtimeConfig.public.VUE_APP_API + url;
  }

  // 服务端渲染的接口，兼容必要的请求头
  if (process.server) {
    // 从cookie中读取参数
    const cookie_token = useCookie('voo-token');
    const distinctId = useCookie('voo-distinctId');
    const anonymousId = useCookie('voo-anonymousId');
    if (cookie_token.value) headerConfig.Authorization = `${cookie_token.value} `;
    if (distinctId.value) headerConfig['distinct-id'] = distinctId.value;
    if (anonymousId.value) headerConfig['anonymous-id'] = anonymousId.value;
  }

  /* 去除 null undefined，避免接口报错。（null通常是接口返回的。→_→） */
  filterNull(data);
  // 根据接口传参数据，添加加密字段
  if (config.isEncrypt) {
    encryptoData(data);
  }
  const axiosConfig: AxiosRequestConfig & {
    _businessConfig?: RequestConfig;
    _lcpLogUrl?: string;
  } = {
    // axios的配置对象
    url: method === 'get' ? `${url}?${qs.stringify(data)}` : url,
    method: method as Method,
    data,
    headers: headerConfig,
    // 【关键】将业务自定义配置挂载到 axios 配置上，拦截器才能读到
    _businessConfig: config,
    _lcpLogUrl: `${method.toUpperCase()}:${originalUrl}`,
  };
  config = config || {};
  /* 错误消息默认弹出，成功消息默认不弹出 */
  config.errorMsg = config.errorMsg === undefined ? true : config.errorMsg;
  config.successMsg = config.successMsg === undefined ? false : config.successMsg;
  config.msgType = config.msgType === undefined ? 'default' : config.msgType;
  const ssrRequestStartedAt = process.server ? Date.now() : 0;
  let ssrRequestEvent: ReturnType<typeof useRequestEvent> | undefined;
  let ssrStatusCode: number | undefined;
  if (process.server) {
    try {
      ssrRequestEvent = useRequestEvent();
    } catch {
      ssrRequestEvent = undefined;
    }
  }
  return new Promise((resolve, reject) => {
    http(axiosConfig)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        ssrStatusCode = 200;
        if (
          res &&
          (res.code === 200 || (config.successWithoutCode && res.code === undefined && res.message))
        ) {
          resolve(res);
          if (config.successMsg && res.msg) {
            if (config.msgType === 'box') {
              ElMessageBox({
                title: '',
                message: res.msg,
                type: 'success',
                center: true,
                confirmButtonText: 'confirm',
                dangerouslyUseHTMLString: true,
              });
            } else {
              ElMessage.success(res.msg);
            }
          }
        }
      })
      .catch((err) => {
        ssrStatusCode = err.code || undefined;
        if (err.code === 404) {
          resolve(err);
        }
        // 401 和 Toast 逻辑已移至插件处理
        reject(err);
      })
      .finally(() => {
        if (!process.server || !ssrRequestStartedAt) return;
        recordSsrBackendRequest(ssrRequestEvent, {
          durationMs: Date.now() - ssrRequestStartedAt,
          method: method.toUpperCase(),
          statusCode: ssrStatusCode,
          url: originalUrl,
        });
      });
  });
};
